import { Box, CircularProgress } from '@mui/material';
import React, { useEffect } from 'react';
import {
  AutocompleteArrayInput,
  BooleanInput,
  RadioButtonGroupInput,
  useGetList,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';

export enum APPLY_TYPES {
  CURRENT_ONLY = 'current_only',
  COMPANY_LOCATIONS = 'company_locations',
  FACILITY_LIST = 'facility_list',
  VMS_HS = 'vms_hs',
}

export const APPLY_TYPE_CHOICES = [
  {
    id: APPLY_TYPES.CURRENT_ONLY,
    name: 'Apply to current location only',
  },
  {
    id: APPLY_TYPES.COMPANY_LOCATIONS,
    name: 'Apply to all company locations',
  },
  {
    id: APPLY_TYPES.FACILITY_LIST,
    name: 'Apply to selected locations',
  },
];

const BLOCK_APPLY_TYPE_CHOICES = [
  ...APPLY_TYPE_CHOICES,
  {
    id: APPLY_TYPES.VMS_HS,
    name: 'Apply to VMS or Health System',
  },
];

export const MULTIPLE_REQUESTER_OPTIONS = [
  APPLY_TYPES.FACILITY_LIST,
  APPLY_TYPES.COMPANY_LOCATIONS,
];

type Props = {
  requester?: 'Location' | 'Professional';
  companyId?: number;
  onKindChange: (args: string) => void;
  onIsMultipleChange: (args: boolean) => void;
};

export const VMSHealthSystemInputs = () => {
  const { watch } = useFormContext();
  const requester = watch('requester');
  const requesterType = watch('requester_type');
  const relationshipKind = watch('kind');

  return (requester === 'Location' || requesterType === 'Location') &&
    relationshipKind === 'block' ? (
    <>
      <BooleanInput
        label="Apply to entire VMS facility list"
        source="apply_to_vms_type"
        name="mass_block[apply_to_vms_type]"
        defaultValue={false}
      />
      <BooleanInput
        label="Apply to entire Health System facility list"
        source="apply_to_health_system"
        name="mass_block[apply_to_health_system]"
        defaultValue={false}
      />
      <BooleanInput
        label="Apply to entire MSP facility list"
        source="apply_to_msp"
        name="mass_block[apply_to_msp]"
        defaultValue={false}
      />
    </>
  ) : null;
};

const RequesterInputs = ({ requester, companyId, onKindChange, onIsMultipleChange }: Props) => {
  const { watch, setValue, resetField } = useFormContext();
  const multiRequesterField = 'requester_ids';
  const massBlockField = 'mass_block';

  const applyType = watch('apply_type');
  const requesterType = watch('requester_type');
  const relationshipKind = watch('kind');

  const isLocation = requester === 'Location' || requesterType === 'Location';

  const isBlock = relationshipKind === 'block';
  const isVMSHs = applyType === APPLY_TYPES.VMS_HS;
  const isList = applyType === APPLY_TYPES.FACILITY_LIST;
  const isMultiple = MULTIPLE_REQUESTER_OPTIONS.includes(applyType);

  const applyTypeChoices = isBlock ? BLOCK_APPLY_TYPE_CHOICES : APPLY_TYPE_CHOICES;

  const { data: locations, isLoading } = useGetList(
    'Locations',
    {
      pagination: { page: 1, perPage: 1000 },
      filter: {
        company_id: companyId,
      },
    },
    {
      enabled: !!companyId && isMultiple,
    },
  );

  const handleApplyTypeChanged = () => {
    resetField(massBlockField);

    if (applyType === APPLY_TYPES.COMPANY_LOCATIONS && !isLoading) {
      setValue(
        multiRequesterField,
        locations.map(({ id }) => id),
      );

      return;
    }

    setValue(multiRequesterField, []);
  };

  useEffect(() => {
    if (isLocation) {
      handleApplyTypeChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyType, isLocation, isLoading]);

  useEffect(() => onKindChange(relationshipKind), [relationshipKind]);
  useEffect(() => onIsMultipleChange(isMultiple), [isMultiple]);

  if (isLocation) {
    return (
      <Box>
        <RadioButtonGroupInput
          label=""
          source="apply_type"
          choices={applyTypeChoices}
          row={false}
        />
        {isVMSHs && <VMSHealthSystemInputs />}
        {isList && !isLoading && (
          <AutocompleteArrayInput
            blurOnSelect={false}
            disableCloseOnSelect
            label="Requesters"
            optionText="name"
            source="requester_ids"
            choices={locations}
          />
        )}
        {isLoading && isMultiple && (
          <Box pb={2}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  }

  return null;
};

export default RequesterInputs;
